import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { investmentAPI, fundAPI, documentAPI } from '../services/api';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PortfolioTab from '../components/investment/PortfolioTab';
import TransactionsTab from '../components/investment/TransactionsTab';
import FMVTab from '../components/investment/FMVTab';
import SummaryTab from '../components/investment/SummaryTab';
import DocumentsTab from '../components/investment/DocumentsTab';
import { EntityAuditHistory } from './AuditTrail';

const InvestmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [investment, setInvestment] = useState(null);
  const [fund, setFund] = useState(null);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvestmentDetails();
  }, [id]);

  const fetchInvestmentDetails = async () => {
    try {
      const invRes = await investmentAPI.getById(id);
      setInvestment(invRes.data);
      
      const fundRes = await fundAPI.getById(invRes.data.fund_id);
      setFund(fundRes.data);
    } catch (error) {
      console.error('Failed to fetch investment details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define all tabs with their required module permissions
  const allTabs = [
    { id: 'portfolio', label: 'Portfolio Info', module: 'portfolio' },
    { id: 'transactions', label: 'Transactions', module: 'transactions' },
    { id: 'fmv', label: 'Fair Market Value', module: 'fmv' },
    { id: 'summary', label: 'Investment Summary', module: 'investment_summary' },
    { id: 'documents', label: 'Documentation', module: 'documents' },
  ];

  const ep = user?.effective_permissions || user?.module_permissions || {};
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  // Filter tabs based on user's effective permissions
  const tabs = [
    ...allTabs.filter(tab => ep[tab.module] === true),
    ...(isAdmin ? [{ id: 'history', label: 'History', module: null }] : []),
  ];

  // If current active tab is not accessible, switch to first available tab
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!investment) {
    return <div className="text-center py-12">Investment not found</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/investments')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Investments
      </button>

      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="p-6 border-b">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="investment-name">
            {investment.portfolio_company_name}
          </h1>
          <p className="text-gray-600 mt-2">
            Fund: {fund?.name} | Currency: {investment.investment_currency}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'portfolio' && (
            <PortfolioTab investment={investment} onUpdate={fetchInvestmentDetails} />
          )}
          {activeTab === 'transactions' && <TransactionsTab investmentId={id} currency={investment.investment_currency} investment={investment} />}
          {activeTab === 'fmv' && <FMVTab investmentId={id} currency={investment.investment_currency} investment={investment} />}
          {activeTab === 'summary' && <SummaryTab investmentId={id} currency={investment.investment_currency} investment={investment} />}
          {activeTab === 'documents' && <DocumentsTab investmentId={id} investment={investment} />}
          {activeTab === 'history' && isAdmin && (
            <div data-testid="tab-content-history">
              <EntityAuditHistory entityType="investment" entityId={id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentDetails;
